import { useEffect, useState } from "react";
import { Gift, Clock, Wallet } from "lucide-react";
import AppShell from "@/components/AppShell";
import { api, getErrorMessage } from "@/lib/api";

const STATUS_STYLE = {
  active: "bg-emerald-50 text-emerald-700",
  pending_kyc: "bg-amber-50 text-amber-700",
  expired: "bg-zinc-100 text-cm-grey",
  used: "bg-blue-50 text-cm-blue",
  revoked: "bg-red-50 text-red-600",
};

function fmtInr(v) {
  return Number(v || 0).toLocaleString("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  });
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function MyRewards() {
  const [vouchers, setVouchers] = useState([]);
  const [history, setHistory] = useState([]);
  const [summary, setSummary] = useState(null);
  const [err, setErr] = useState("");
  const [tab, setTab] = useState("rewards");

  async function load() {
    try {
      const { data } = await api.get("/api/rewards/my-vouchers");
      setVouchers(data.items || []);
      setSummary({
        active_vouchers: data.active_vouchers ?? 0,
        pending_kyc: data.pending_kyc ?? 0,
        bonus_balances: data.bonus_balances || [],
      });
      setErr("");
    } catch (e) {
      setErr(getErrorMessage(e));
    }
  }

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (tab !== "history" || history.length) return undefined;
    let cancelled = false;
    api.get("/api/rewards/history?limit=50")
      .then(({ data }) => {
        if (!cancelled) setHistory(data.items || []);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [tab, history.length]);

  const bonusBalances = summary?.bonus_balances || [];

  return (
    <AppShell>
      <div className="max-w-4xl mx-auto">
        <h1 className="text-[28px] font-bold text-cm-navy flex items-center gap-2">
          <Gift className="text-cm-blue" size={28} />
          My Rewards
        </h1>
        <p className="text-cm-grey text-[14px] mt-1 mb-6">
          Bonus wallet balances are for trading benefits only and cannot be withdrawn directly.
        </p>

        {err && <p className="text-red-600 text-[13px] mb-4">{err}</p>}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-[16px] border border-[var(--cm-border)] p-5">
            <p className="text-[12px] text-cm-grey font-semibold">Active rewards</p>
            <p className="text-[24px] font-bold text-cm-navy mt-1">{summary?.active_vouchers ?? "—"}</p>
          </div>
          <div className="bg-white rounded-[16px] border border-[var(--cm-border)] p-5">
            <p className="text-[12px] text-cm-grey font-semibold">Pending KYC</p>
            <p className="text-[24px] font-bold text-amber-600 mt-1">{summary?.pending_kyc ?? "—"}</p>
          </div>
          <div className="bg-white rounded-[16px] border border-[var(--cm-border)] p-5">
            <p className="text-[12px] text-cm-grey font-semibold flex items-center gap-1">
              <Wallet size={14} /> Bonus wallet
            </p>
            {bonusBalances.length === 0 ? (
              <p className="text-[14px] text-cm-grey mt-2">No bonus balance yet</p>
            ) : (
              bonusBalances.map((b) => (
                <p key={b.currency} className="text-[18px] font-bold text-cm-navy mt-1">
                  {Number(b.available).toLocaleString("en-IN", { maximumFractionDigits: 4 })} {b.currency}
                </p>
              ))
            )}
          </div>
        </div>

        <div className="inline-flex bg-white border border-[var(--cm-border)] rounded-[10px] p-0.5 mb-4">
          {[
            { k: "rewards", label: "My vouchers" },
            { k: "history", label: "Reward history" },
          ].map(({ k, label }) => (
            <button
              key={k}
              type="button"
              onClick={() => setTab(k)}
              className={`px-4 py-2 rounded-[8px] text-[13px] font-semibold ${
                tab === k ? "bg-cm-blue text-white" : "text-cm-grey hover:text-cm-navy"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "rewards" && (
          <div className="bg-white rounded-[16px] border border-[var(--cm-border)] divide-y divide-[var(--cm-border)]">
            {vouchers.length === 0 ? (
              <p className="p-8 text-center text-cm-grey text-[14px]">No rewards assigned yet.</p>
            ) : (
              vouchers.map((v) => (
                <div key={v.user_voucher_id} className="p-5 flex flex-wrap justify-between gap-3">
                  <div>
                    <p className="font-bold text-cm-navy">{v.voucher_name || v.voucher_code}</p>
                    <p className="text-[13px] text-cm-grey mt-0.5">
                      {v.reward_amount} {v.reward_currency}
                    </p>
                    <p className="text-[12px] text-cm-grey mt-2 flex items-center gap-1">
                      <Clock size={12} />
                      Expires {fmtDate(v.expires_at)}
                    </p>
                  </div>
                  <span className={`self-start px-2.5 py-1 rounded-full text-[11px] font-semibold ${STATUS_STYLE[v.status] || "bg-zinc-100"}`}>
                    {String(v.status).replace(/_/g, " ")}
                  </span>
                </div>
              ))
            )}
          </div>
        )}

        {tab === "history" && (
          <div className="bg-white rounded-[16px] border border-[var(--cm-border)] overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="text-cm-grey text-left">
                <tr>
                  <th className="px-5 py-3">Date</th>
                  <th className="px-5 py-3">Action</th>
                  <th className="px-5 py-3">Amount</th>
                  <th className="px-5 py-3">Trigger</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--cm-border)]">
                {history.length === 0 ? (
                  <tr><td colSpan={4} className="px-5 py-8 text-center text-cm-grey">No history</td></tr>
                ) : (
                  history.map((h) => (
                    <tr key={h.reward_tx_id}>
                      <td className="px-5 py-3 whitespace-nowrap">{fmtDate(h.created_at)}</td>
                      <td className="px-5 py-3 capitalize">{h.action?.replace(/_/g, " ")}</td>
                      <td className="px-5 py-3 tabular-nums">
                        {h.currency === "INR" ? fmtInr(h.amount) : `${h.amount} ${h.currency}`}
                      </td>
                      <td className="px-5 py-3 text-cm-grey">{h.trigger_event}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AppShell>
  );
}
